import React, { useEffect, useMemo, useState } from "react";
import {
  Paperclip,
  Upload,
  Trash2,
  ExternalLink,
  RefreshCcw,
} from "lucide-react";
import { supabase } from "../../../Services/supabaseClient";
import "./fixedAssets.css";

function bytesToSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  const units = ["B", "KB", "MB", "GB"];
  let b = Number(bytes);
  let i = 0;
  while (b >= 1024 && i < units.length - 1) {
    b /= 1024;
    i += 1;
  }
  return `${b.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

function safeName(name) {
  return String(name || "file").replace(/[^\w.\-() ]+/g, "_");
}

export default function AssetAttachments({ companyId, assetId }) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const bucket = "fixed-assets";

  async function load() {
    if (!companyId || !assetId) return;
    setErr("");

    const { data, error } = await supabase
      .from("fixed_asset_files")
      .select("id, file_path, file_name, mime_type, size_bytes, created_at")
      .eq("company_id", companyId)
      .eq("asset_id", assetId)
      .order("created_at", { ascending: false });

    if (error) setErr(error.message);
    else setFiles(data || []);
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, assetId]);

  const totalCount = files.length;

  const accept = useMemo(
    () =>
      [
        "application/pdf",
        "image/png",
        "image/jpeg",
        "image/webp",
        "text/plain",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ].join(","),
    []
  );

  async function uploadFile(file) {
    if (!file) return;
    if (!companyId || !assetId) {
      setErr("Missing companyId or assetId");
      return;
    }

    setBusy(true);
    setErr("");

    try {
      const ts = Date.now();
      const path = `${companyId}/${assetId}/${ts}_${safeName(file.name)}`;

      const { error: upErr } = await supabase.storage
        .from(bucket)
        .upload(path, file, {
          contentType: file.type || "application/octet-stream",
          upsert: false,
        });

      if (upErr) throw upErr;

      const { error: dbErr } = await supabase.from("fixed_asset_files").insert({
        company_id: companyId,
        asset_id: assetId,
        file_path: path,
        file_name: file.name,
        mime_type: file.type,
        size_bytes: file.size,
      });

      if (dbErr) {
        // If DB insert fails, cleanup storage object to avoid orphan
        await supabase.storage.from(bucket).remove([path]);
        throw dbErr;
      }

      await load();
    } catch (e) {
      setErr(e.message || "Upload failed");
    } finally {
      setBusy(false);
    }
  }

  async function openFileSigned(filePath) {
    setErr("");
    try {
      const { data, error } = await supabase.storage
        .from(bucket)
        .createSignedUrl(filePath, 60 * 10); // 10 minutes

      if (error) throw error;
      window.open(data.signedUrl, "_blank", "noopener,noreferrer");
    } catch (e) {
      setErr(e.message || "Failed to open file");
    }
  }

  async function removeFile(row) {
    const ok = window.confirm(
      "Delete this attachment? This removes it from storage and the register."
    );
    if (!ok) return;

    setBusy(true);
    setErr("");

    try {
      // Delete storage object first
      const { error: delObjErr } = await supabase.storage
        .from(bucket)
        .remove([row.file_path]);
      if (delObjErr) throw delObjErr;

      // Delete DB row
      const { error: delRowErr } = await supabase
        .from("fixed_asset_files")
        .delete()
        .eq("id", row.id)
        .eq("company_id", companyId);

      if (delRowErr) throw delRowErr;

      await load();
    } catch (e) {
      setErr(e.message || "Delete failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fa-panel" style={{ marginTop: 12 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 10,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Paperclip size={18} />
          <div>
            <div style={{ fontWeight: 900 }}>Attachments</div>
            <div style={{ opacity: 0.75, fontSize: 13 }}>
              Store receipts, invoices, photos, and supporting documents.
            </div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <button className="fa-btn" onClick={load} disabled={busy}>
            <RefreshCcw size={16} /> Refresh
          </button>

          <label
            className={`fa-btn fa-btn-primary ${busy ? "fa-btn-disabled" : ""}`}
            style={{ cursor: busy ? "not-allowed" : "pointer" }}
          >
            <Upload size={16} /> {busy ? "Uploading…" : "Upload"}
            <input
              type="file"
              accept={accept}
              disabled={busy}
              style={{ display: "none" }}
              onChange={(e) => uploadFile(e.target.files?.[0])}
            />
          </label>
        </div>
      </div>

      {err && (
        <div style={{ marginTop: 10, color: "#ffb4b4", fontSize: 13 }}>
          {err}
        </div>
      )}

      <div style={{ marginTop: 12 }}>
        {totalCount === 0 ? (
          <div style={{ opacity: 0.75 }}>No attachments yet.</div>
        ) : (
          <table className="fa-table">
            <thead>
              <tr>
                <th>File</th>
                <th style={{ width: 160 }}>Size</th>
                <th style={{ width: 220 }}>Uploaded</th>
                <th style={{ width: 200 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {files.map((f) => (
                <tr key={f.id}>
                  <td>
                    <div style={{ fontWeight: 800 }}>{f.file_name}</div>
                    <div style={{ opacity: 0.75, fontSize: 12 }}>
                      {f.mime_type || "—"}
                    </div>
                  </td>
                  <td>{bytesToSize(f.size_bytes)}</td>
                  <td>
                    {f.created_at
                      ? new Date(f.created_at).toLocaleString()
                      : "—"}
                  </td>
                  <td>
                    <div className="fa-row-actions">
                      <button
                        className="fa-btn"
                        onClick={() => openFileSigned(f.file_path)}
                        disabled={busy}
                      >
                        <ExternalLink size={16} /> Open
                      </button>
                      <button
                        className="fa-btn"
                        onClick={() => removeFile(f)}
                        disabled={busy}
                      >
                        <Trash2 size={16} /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
